import { useEffect, useRef } from "react";

const REQUEST = "clearsquare:identity-request";
const REPLY = "clearsquare:identity";
// Shorter than the time an app waits for an answer, so it hears "unavailable" rather than nothing.
const WAIT_MS = 12000;

// Seconds until the token expires, read from its payload (the app checks the signature; this only avoids handing
// out a token that is about to run out).
function secondsLeft(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp - Date.now() / 1000;
  } catch (error) {
    return 0;
  }
}

/**
 * Signs the user in to a custom app that asks for it. The page data carries a short-lived token, signed by the API,
 * naming the signed-in user (content.app_token). When the app posts { type: "clearsquare:identity-request",
 * requestId }, it gets { type: "clearsquare:identity", requestId, token } back, sent only to the app's own origin.
 * Each token is handed out once; for the next request the page data is reloaded in the background to get a new one.
 * If no token can be had, the reply carries error: "unavailable" instead.
 */
export function useCustomAppIdentity({ content, iframeRef, frameUrl }) {
  const current = useRef({ token: null, used: false });
  const pending = useRef(null); // { requestId, timer } while waiting for a fresh token

  const reply = (requestId, message) => {
    const frame = iframeRef.current?.contentWindow;
    let origin;
    try {
      origin = new URL(frameUrl).origin;
    } catch (error) {
      return;
    }
    if (frame) frame.postMessage({ type: REPLY, requestId, ...message }, origin);
  };

  const handOut = (requestId) => {
    const { token, used } = current.current;
    if (!token || used || secondsLeft(token) < 20) return false;
    current.current.used = true;
    reply(requestId, { token });
    return true;
  };

  const settle = (message) => {
    const waiting = pending.current;
    if (!waiting) return;
    pending.current = null;
    clearTimeout(waiting.timer);
    if (message) reply(waiting.requestId, message);
    else if (!handOut(waiting.requestId)) reply(waiting.requestId, { error: "unavailable" });
  };

  // New page data: a new token, if the API could sign one. Page data reloaded for another reason carries the same
  // token, which stays used.
  useEffect(() => {
    const token = content?.app_token || null;
    const changed = token !== current.current.token;
    if (changed) current.current = { token, used: false };
    if (changed || !token) settle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    const onMessage = (event) => {
      const frame = iframeRef.current;
      if (!frame || event.source !== frame.contentWindow || event.data?.type !== REQUEST) return;
      const requestId = String(event.data.requestId || "");
      if (handOut(requestId)) return;
      if (pending.current) clearTimeout(pending.current.timer);
      pending.current = { requestId, timer: setTimeout(() => settle({ error: "unavailable" }), WAIT_MS) };
      window.dispatchEvent(new Event("custom-app:token-expired"));
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      if (pending.current) clearTimeout(pending.current.timer);
      pending.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameUrl]);
}
